/**
 * useProviderInstances - Hook para gerenciamento de instâncias de providers
 * 
 * Funcionalidades:
 * - Listagem de instâncias (admin: todas, tenant: próprias)
 * - CRUD de instâncias
 * - Liberação de instâncias pelo admin
 * - Busca de instâncias disponíveis para seleção no tenant
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import { showToast } from "@/lib/toast";

// Interface para instância de provider
export interface ProviderInstance {
  id: string;
  tenant_id: string;
  provider_id: string;
  token: string | null;
  instance_id: string | null;
  instance_name: string | null;
  status: 'pending' | 'released' | 'in_use' | 'cancelled';
  stripe_checkout_session_id: string | null;
  stripe_subscription_item_id: string | null;
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded' | 'free_trial' | 'courtesy';
  inbox_id: string | null;
  source_type: 'plan' | 'addon' | 'manual';
  addon_id: string | null;
  tenant_addon_id: string | null;
  requested_at: string;
  released_at: string | null;
  released_by: string | null;
  activated_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  // Joins
  tenant?: {
    id: string;
    name: string;
    slug: string;
  };
  provider?: {
    id: string;
    name: string;
    display_name: string | null;
    slug: string;
  };
  // Dados da inbox vinculada (inclui status para exibição no admin)
  inbox?: {
    id: string;
    name: string;
    status: 'active' | 'inactive' | 'connecting' | 'error' | 'disconnected';
  } | null;
}

// Interface para criação de instância
export interface CreateInstanceData {
  tenant_id: string;
  provider_id: string;
  source_type?: 'plan' | 'addon' | 'manual';
  payment_status?: 'pending' | 'paid' | 'free_trial' | 'courtesy';
  notes?: string;
}

// Interface para atualização de instância
export interface UpdateInstanceData {
  token?: string;
  instance_id?: string;
  instance_name?: string;
  credentials?: Record<string, string>; // JSONB flexível para credenciais dinâmicas
  status?: 'pending' | 'released' | 'in_use' | 'cancelled';
  payment_status?: 'pending' | 'paid' | 'failed' | 'refunded' | 'free_trial' | 'courtesy';
  notes?: string;
  provider_id?: string; // Permite alterar o provider da instância (somente quando não está em uso)
}

/**
 * Hook para gerenciamento de instâncias de providers (Admin)
 */
export function useProviderInstancesAdmin(providerId?: string) {
  const queryClient = useQueryClient();

  // Buscar todas as instâncias (com filtro opcional por provider)
  const { data: instances, isLoading, error, refetch } = useQuery({
    queryKey: ['provider-instances-admin', providerId],
    queryFn: async () => {
      logger.info('useProviderInstances', 'Buscando instâncias (admin)', { providerId });
      
      let query = supabase
        .from('provider_instances')
        .select(`
          *,
          tenant:tenants(id, name, slug),
          provider:api_providers(id, name, display_name, slug),
          inbox:inboxes(id, name, status)
        `)
        .order('created_at', { ascending: false });

      if (providerId) {
        query = query.eq('provider_id', providerId);
      }

      const { data, error } = await query;

      if (error) {
        logger.error('useProviderInstances', 'Erro ao buscar instâncias', error);
        throw error;
      }

      return data as ProviderInstance[];
    },
  });

  // Criar nova instância
  const createInstance = useMutation({
    mutationFn: async (data: CreateInstanceData) => {
      logger.info('useProviderInstances', 'Criando instância', data);
      
      const { data: result, error } = await supabase
        .from('provider_instances')
        .insert({
          ...data,
          source_type: data.source_type || 'manual',
          payment_status: data.payment_status || 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances-admin'] });
      showToast.success('Instância criada com sucesso');
    },
    onError: (error) => {
      logger.error('useProviderInstances', 'Erro ao criar instância', error);
      showToast.error('Erro ao criar instância');
    },
  });

  // Atualizar instância
  const updateInstance = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateInstanceData }) => {
      logger.info('useProviderInstances', 'Atualizando instância', { id, data });
      
      const { data: result, error } = await supabase
        .from('provider_instances')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances-admin'] });
      showToast.success('Instância atualizada com sucesso');
    },
    onError: (error) => {
      logger.error('useProviderInstances', 'Erro ao atualizar instância', error);
      
      // Verifica se é erro de duplicidade - nesse caso, deixa o componente tratar
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isDuplicateError = errorMessage.includes('idx_provider_instances_unique_instance') || 
                               errorMessage.includes('duplicate key');
      
      // Só mostra toast genérico se NÃO for erro de duplicidade
      if (!isDuplicateError) {
        showToast.error('Erro ao atualizar instância');
      }
    },
  });

  // Liberar instância para tenant (com credenciais dinâmicas)
  const releaseInstance = useMutation({
    mutationFn: async ({ id, credentials, instanceName }: { 
      id: string; 
      credentials: Record<string, string>; // Objeto flexível com credenciais
      instanceName?: string;
    }) => {
      logger.info('useProviderInstances', 'Liberando instância', { id, credentialKeys: Object.keys(credentials) });
      
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data: result, error } = await supabase
        .from('provider_instances')
        .update({
          credentials, // JSONB com todos os campos dinâmicos
          // Manter token e instance_id para compatibilidade legada
          token: credentials.token || credentials.api_token || null,
          instance_id: credentials.instance_id || null,
          instance_name: instanceName || null,
          status: 'released',
          released_at: new Date().toISOString(),
          released_by: user?.id || null,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances-admin'] });
      showToast.success('Instância liberada com sucesso');
    },
    onError: (error) => {
      logger.error('useProviderInstances', 'Erro ao liberar instância', error);
      
      // Verifica se é erro de duplicidade - nesse caso, deixa o componente tratar
      const errorMessage = error instanceof Error ? error.message : String(error);
      const isDuplicateError = errorMessage.includes('idx_provider_instances_unique_instance') || 
                               errorMessage.includes('duplicate key');
      
      // Só mostra toast genérico se NÃO for erro de duplicidade
      if (!isDuplicateError) {
        showToast.error('Erro ao liberar instância');
      }
    },
  });

  // Cancelar instância
  const cancelInstance = useMutation({
    mutationFn: async (id: string) => {
      logger.info('useProviderInstances', 'Cancelando instância', { id });
      
      const { data: result, error } = await supabase
        .from('provider_instances')
        .update({ status: 'cancelled' })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances-admin'] });
      showToast.success('Instância cancelada');
    },
    onError: (error) => {
      logger.error('useProviderInstances', 'Erro ao cancelar instância', error);
      showToast.error('Erro ao cancelar instância');
    },
  });

  // Estatísticas rápidas
  const stats = {
    total: instances?.length || 0,
    pending: instances?.filter(i => i.status === 'pending').length || 0,
    released: instances?.filter(i => i.status === 'released').length || 0,
    inUse: instances?.filter(i => i.status === 'in_use').length || 0,
    cancelled: instances?.filter(i => i.status === 'cancelled').length || 0,
  };

  return {
    instances,
    isLoading,
    error,
    refetch,
    stats,
    createInstance,
    updateInstance,
    releaseInstance,
    cancelInstance,
  };
}

/**
 * Hook para tenant buscar instâncias disponíveis
 */
export function useAvailableInstances(tenantId: string | undefined, providerSlug: string | undefined) {
  const queryClient = useQueryClient();

  // Buscar instâncias liberadas (released) para o tenant e provider específico
  // Query com polling de 5s para atualização automática quando admin liberar instâncias
  const { data: availableInstances, isLoading, refetch } = useQuery({
    queryKey: ['available-instances', tenantId, providerSlug],
    queryFn: async () => {
      if (!tenantId || !providerSlug) return [];
      
      logger.info('useProviderInstances', 'Buscando instâncias disponíveis', { tenantId, providerSlug });

      // Primeiro buscar o provider_id pelo slug
      const { data: provider, error: providerError } = await supabase
        .from('api_providers')
        .select('id')
        .eq('slug', providerSlug)
        .single();

      if (providerError || !provider) {
        logger.warn('useProviderInstances', 'Provider não encontrado', { providerSlug });
        return [];
      }

      // Buscar instâncias liberadas para este tenant e provider
      // Inclui 'credentials' para copiar todas as credenciais (api_token, channel_token, etc) para inbox_credentials
      const { data, error } = await supabase
        .from('provider_instances')
        .select('id, token, instance_id, instance_name, credentials')
        .eq('tenant_id', tenantId)
        .eq('provider_id', provider.id)
        .eq('status', 'released');

      if (error) {
        logger.error('useProviderInstances', 'Erro ao buscar instâncias disponíveis', error);
        return [];
      }

      return data || [];
    },
    enabled: !!tenantId && !!providerSlug,
    refetchInterval: 5000, // Polling a cada 5 segundos
    staleTime: 2000,       // Dados considerados "frescos" por 2s
  });

  // Verificar se há instâncias pendentes
  // Query com polling de 5s para verificar instâncias pendentes
  const { data: hasPendingInstances } = useQuery({
    queryKey: ['pending-instances', tenantId, providerSlug],
    queryFn: async () => {
      if (!tenantId || !providerSlug) return false;

      const { data: provider } = await supabase
        .from('api_providers')
        .select('id')
        .eq('slug', providerSlug)
        .single();

      if (!provider) return false;

      const { count } = await supabase
        .from('provider_instances')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .eq('provider_id', provider.id)
        .eq('status', 'pending');

      return (count || 0) > 0;
    },
    enabled: !!tenantId && !!providerSlug,
    refetchInterval: 5000, // Polling a cada 5 segundos
    staleTime: 2000,       // Dados considerados "frescos" por 2s
  });

  // Marcar instância como em uso via RPC segura
  // Utiliza função RPC com SECURITY DEFINER para evitar problemas de RLS
  // IMPORTANTE: Passa p_tenant_hint para arquitetura de segurança tenant v2.0
  const useInstance = useMutation({
    mutationFn: async ({ instanceId, inboxId }: { instanceId: string; inboxId: string }) => {
      logger.info('useProviderInstances', 'Vinculando instância à inbox via RPC', { instanceId, inboxId, tenantId });
      
      const { data, error } = await supabase
        .rpc('link_instance_to_inbox', {
          p_instance_id: instanceId,
          p_inbox_id: inboxId,
          p_tenant_hint: tenantId // Parâmetro de segurança tenant v2.0
        });

      if (error) {
        logger.error('useProviderInstances', 'Erro na RPC link_instance_to_inbox', error);
        throw error;
      }
      
      // Cast para tipo esperado e verificar se a RPC retornou sucesso
      const result = data as { success: boolean; error?: string; instance_id?: string; inbox_id?: string };
      if (!result?.success) {
        const errorMsg = result?.error || 'Erro desconhecido ao vincular instância';
        logger.error('useProviderInstances', 'RPC retornou erro', { error: errorMsg });
        throw new Error(errorMsg);
      }
      
      logger.info('useProviderInstances', 'Instância vinculada com sucesso', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['available-instances'] });
    },
  });

  // Liberar instância quando inbox é desativada
  const releaseFromInbox = useMutation({
    mutationFn: async (inboxId: string) => {
      logger.info('useProviderInstances', 'Liberando instância da inbox', { inboxId });
      
      const { data, error } = await supabase
        .from('provider_instances')
        .update({
          status: 'released',
          inbox_id: null,
        })
        .eq('inbox_id', inboxId)
        .select()
        .single();

      if (error && error.code !== 'PGRST116') throw error; // Ignora se não encontrar
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['available-instances'] });
    },
  });

  return {
    availableInstances: availableInstances || [],
    hasPendingInstances: hasPendingInstances || false,
    isLoading,
    refetch,
    useInstance,
    releaseFromInbox,
  };
}
